import { defu } from "defu";
import { useFetch, type UseFetchOptions } from "nuxt/app";
import * as Sentry from "@sentry/nuxt";
import { useConfig } from "~/composables/useConfig";

interface useApiClientOptions<O, T> extends UseFetchOptions<unknown> {
  mapper?: DataMapper<O, T>;
}

export const useApiClient = async <T, O = T>(
  url: string | (() => string),
  options: useApiClientOptions<O, T> = {},
  mockResponse?: {
    data: O;
    delay?: number;
    success?: boolean;
    message?: string;
  }
): Promise<ResponseApi<T>> => {
  try {
    if (mockResponse !== undefined) {
      // eslint-disable-next-line no-console
      console.info(
        `Mocking API call to ${typeof url === "string" ? url : url()}`
      );
      if (options.body) {
        // eslint-disable-next-line no-console
        console.info("Body:", options.body);
      }
      const delay = mockResponse.delay ?? 0;
      const dataResponse = options.mapper
        ? options.mapper.map(mockResponse.data)
        : (mockResponse.data as unknown as T);
      const response = {
        data: dataResponse,
        error: undefined,
        success: mockResponse.success ?? true,
        message: mockResponse.message
      };

      if (delay > 0) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(response);
          }, delay);
        });
      }

      return response;
    }
    const { config } = useConfig();
    const defaults: UseFetchOptions<unknown> = {
      baseURL: config.API_URL,
      credentials: "include",
      // Generate a unique key for each request to prevent caching issues
      key: `${typeof url === "string" ? url : url()}-${Date.now()}-${Math.random()}`
    };

    const params = defu(options, defaults);

    const { data, error } = await useFetch(url, params);
    if (error.value) {
      return {
        data: undefined as unknown as T,
        error: {
          details: (error.value as any)?.data?.error?.details,
          appCode: (error.value as any)?.data?.error?.code,
          context: {
            url: typeof url === "string" ? url : url(),
            method: options?.method as string | undefined,
            params: options?.params as unknown | undefined,
            statusCode: error.value.statusCode as number
          }
        },
        success: false,
        message: (error.value as any)?.data?.message
      };
    }
    if (options.mapper) {
      const mappedData = options.mapper.map((data.value as any)?.data as O);
      return {
        data: mappedData,
        error: undefined,
        success: (data.value as any)?.success,
        message: (data.value as any)?.message
      };
    }
    return {
      data: (data.value as any)?.data,
      error: undefined,
      success: (data.value as any)?.success,
      message: (data.value as any)?.message
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("useApiClient error:", err);
    Sentry.captureException(err, {
      extra: {
        url: typeof url === "string" ? url : url(),
        method: options?.method,
        params: options?.params
      }
    });
    return {
      data: undefined as unknown as T,
      error: {
        details: (err as any)?.message,
        appCode: "API_CLIENT_ERROR",
        context: {
          url: typeof url === "string" ? url : url(),
          method: options?.method as string | undefined,
          statusCode: 500
        }
      },
      success: false,
      message: "Error en la solicitud API"
    };
  }
};
